import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import * as XLSX from 'xlsx'

const fmt = v => v==null?'—':Number(v).toLocaleString('en-US',{minimumFractionDigits:0,maximumFractionDigits:0})
function monthKey(d){return d.toISOString().slice(0,7)}
function monthLabel(ym){const[y,m]=ym.split('-');return['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][parseInt(m)-1]+" '"+y.slice(2)}
function firstOfMonth(ym){return ym+'-01'}

export default function Scheduling({ projectId: initProjectId }) {
  const [projects, setProjects]         = useState([])
  const [projectId, setProjectId]       = useState(null)
  const [projectName, setProjectName]   = useState('')
  const [settings, setSettings]         = useState(null)
  const [showSettings, setShowSettings] = useState(false)
  const [settingsForm, setSettingsForm] = useState({ project_start_month:'', project_end_month:'' })
  const [months, setMonths]             = useState([])
  const [data, setData]                 = useState({}) // { [ym]: { planned_cash_in, planned_budget, actual_progress, planned_progress } }
  const [dirty, setDirty]               = useState({})
  const [saving, setSaving]             = useState(false)
  const [loading, setLoading]           = useState(false)
  const [showModal, setShowModal]       = useState(false) // single month entry modal
  const [modalYm, setModalYm]           = useState(null)
  const [modalForm, setModalForm]       = useState({ planned_cash_in:'', planned_budget:'', actual_progress:'', planned_progress:'', client_invoice:'', certified_amount:'' })

  useEffect(()=>{ fetchProjects() },[])
  useEffect(()=>{ if(initProjectId){ setProjectId(initProjectId) } },[initProjectId])
  useEffect(()=>{ if(projectId) loadAll(projectId) },[projectId])

  async function fetchProjects(){
    const{data}=await supabase.from('projects').select('id,name,organizations(name)').order('name')
    setProjects(data||[])
  }

  async function loadAll(pid){
    setLoading(true)
    await loadSettings(pid)
    await loadData(pid)
    setLoading(false)
  }

  async function loadSettings(pid){
    const{data}=await supabase.from('cashflow_settings').select('*').eq('project_id',pid).maybeSingle()
    setSettings(data)
    if(data?.project_start_month && data?.project_end_month){
      setMonths(buildMonths(data.project_start_month,data.project_end_month))
      setSettingsForm({ project_start_month:data.project_start_month.slice(0,7), project_end_month:data.project_end_month.slice(0,7) })
    }
  }

  function buildMonths(start,end){
    const ms=[];const s=new Date(start);s.setDate(1);const e=new Date(end);e.setDate(1)
    while(s<=e){ms.push(monthKey(s));s.setMonth(s.getMonth()+1)}
    return ms
  }

  async function saveSettings(){
    const payload={ project_id:projectId, project_start_month:firstOfMonth(settingsForm.project_start_month), project_end_month:firstOfMonth(settingsForm.project_end_month) }
    const{data:ex}=await supabase.from('cashflow_settings').select('id').eq('project_id',projectId).maybeSingle()
    if(ex) await supabase.from('cashflow_settings').update(payload).eq('project_id',projectId)
    else await supabase.from('cashflow_settings').insert(payload)
    setShowSettings(false); loadSettings(projectId)
  }

  async function loadData(pid){
    const{data}=await supabase.from('cash_in_planning').select('*').eq('project_id',pid)
    const map={}
    for(const r of (data||[])){
      const ym=r.period_month.slice(0,7)
      map[ym]={ planned_cash_in:parseFloat(r.planned_cash_in)||0, planned_budget:parseFloat(r.planned_budget)||0, actual_progress:parseFloat(r.actual_progress_pct)||0, planned_progress:parseFloat(r.planned_progress_pct)||0, client_invoice:parseFloat(r.client_invoice)||0, certified_amount:parseFloat(r.certified_amount)||0, id:r.id }
    }
    setData(map)
  }

  function handleCell(ym, field, val){
    const num = (field==='planned_cash_in'||field==='planned_budget'||field==='client_invoice'||field==='certified_amount') ? (parseFloat(val)||0) : Math.min(100, Math.max(0, parseFloat(val)||0))
    setData(d=>({...d,[ym]:{...(d[ym]||{}),[field]:num}}))
    setDirty(d=>({...d,[`${ym}|${field}`]:true}))
  }

  async function saveAll(){
    setSaving(true)
    for(const ym of months){
      const keys = ['planned_cash_in','planned_budget','actual_progress','planned_progress','client_invoice','certified_amount']
      if(!keys.some(k=>dirty[`${ym}|${k}`])) continue
      const row = data[ym]||{}
      const payload={ project_id:projectId, period_month:firstOfMonth(ym), planned_cash_in:row.planned_cash_in||0, planned_budget:row.planned_budget||0, actual_progress_pct:row.actual_progress||0, planned_progress_pct:row.planned_progress||0, client_invoice:row.client_invoice||0, certified_amount:row.certified_amount||0 }
      if(row.id) await supabase.from('cash_in_planning').update(payload).eq('id',row.id)
      else {
        const{data:ins}=await supabase.from('cash_in_planning').insert(payload).select('id').single()
        if(ins) setData(d=>({...d,[ym]:{...d[ym],id:ins.id}}))
      }
    }
    setDirty({}); setSaving(false)
  }

  function openModal(ym){
    setModalYm(ym)
    const row=data[ym]||{}
    setModalForm({ planned_cash_in:row.planned_cash_in||'', planned_budget:row.planned_budget||'', actual_progress:row.actual_progress||'', planned_progress:row.planned_progress||'', client_invoice:row.client_invoice||'', certified_amount:row.certified_amount||'' })
    setShowModal(true)
  }

  function saveModal(){
    const row=modalForm
    handleCell(modalYm,'planned_cash_in',row.planned_cash_in)
    handleCell(modalYm,'planned_budget',row.planned_budget)
    handleCell(modalYm,'actual_progress',row.actual_progress)
    handleCell(modalYm,'planned_progress',row.planned_progress)
    handleCell(modalYm,'client_invoice',row.client_invoice)
    handleCell(modalYm,'certified_amount',row.certified_amount)
    setShowModal(false)
  }

  // Column keys, by their canonical header name in the uploaded Excel file.
  // Header-based mapping (not positional): only columns actually present in the
  // uploaded file are read and updated. Any column absent from the file is left
  // completely untouched — the existing database value for that field is preserved,
  // never overwritten with 0/NULL/empty. This is what makes partial uploads safe
  // (e.g. a file containing only "Month" + "Certified Amount" + "Client Invoice").
  const UPLOAD_COLUMN_MAP = {
    'Month':               'month',              // required — identifies the row
    'Planned Cash In':      'planned_cash_in',
    'Actual Progress %':    'actual_progress',
    'Planned Progress %':   'planned_progress',
    'Client Invoice':       'client_invoice',
    'Planned Budget':       'planned_budget',
    'Certified Amount':     'certified_amount',
  }
  const PCT_FIELDS = new Set(['actual_progress', 'planned_progress'])

  function handleUpload(e){
    const file=e.target.files[0]; if(!file) return
    const reader=new FileReader()
    reader.onload=ev=>{
      const wb=XLSX.read(ev.target.result,{type:'binary'})
      const ws=wb.Sheets[wb.SheetNames[0]]
      // Read with header:1 to get the raw header row, so we can map by name
      // regardless of column order or which columns are present.
      const aoa=XLSX.utils.sheet_to_json(ws,{header:1,defval:null})
      if(aoa.length===0) return
      const headerRow = aoa[0].map(h => String(h||'').trim())
      // Build: for each column index present in the file, which internal field it maps to.
      const indexToField = {}
      headerRow.forEach((h, idx) => {
        const field = UPLOAD_COLUMN_MAP[h]
        if (field) indexToField[idx] = field
      })
      const monthColIndex = headerRow.findIndex(h => UPLOAD_COLUMN_MAP[h] === 'month')
      if (monthColIndex === -1) {
        alert('Upload file must contain a "Month" column.')
        return
      }

      const newData={...data}; const newDirty={...dirty}
      for(let i=1;i<aoa.length;i++){
        const row=aoa[i]
        const rawMonth = row[monthColIndex]
        if(!rawMonth) continue
        const ym=String(rawMonth).slice(0,7)
        // Start from the existing row (preserves every field not present in the file).
        // If this month doesn't exist yet in `data`, start from an empty object —
        // only the columns actually in the file will be set for it.
        if(!newData[ym]) newData[ym]={...(data[ym]||{})}

        for(const [idx, field] of Object.entries(indexToField)){
          if (field === 'month') continue
          const cellIdx = parseInt(idx)
          const raw = row[cellIdx]
          // A blank cell for a present column is treated as "no change" too —
          // only a column entirely absent from the header, or an explicit value,
          // affects the field. This avoids accidentally zeroing a value via an
          // empty cell in a column the uploader did include for other rows.
          if (raw === null || raw === undefined || raw === '') continue
          let num = parseFloat(raw)||0
          if (PCT_FIELDS.has(field)) num = Math.min(100, Math.max(0, num))
          newData[ym][field] = num
          newDirty[`${ym}|${field}`]=true
        }
      }
      setData(newData); setDirty(newDirty)
    }
    reader.readAsBinaryString(file)
  }

  function downloadTemplate(){
    const hdr=['Month','Planned Cash In','Actual Progress %','Planned Progress %','Client Invoice','Planned Budget','Certified Amount']
    const rows=months.map(ym=>[ym, data[ym]?.planned_cash_in||0, data[ym]?.actual_progress||0, data[ym]?.planned_progress||0, data[ym]?.client_invoice||0, data[ym]?.planned_budget||0, data[ym]?.certified_amount||0])
    const ws=XLSX.utils.aoa_to_sheet([hdr,...rows])
    ws['!cols']=[{wch:12},{wch:18},{wch:18},{wch:18},{wch:18},{wch:18},{wch:18}]
    const wb=XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb,ws,'Schedule')
    XLSX.writeFile(wb,'schedule_template.xlsx')
  }

  const thS={padding:'8px 10px',fontSize:11,fontWeight:700,background:'#1B3A6B',color:'#fff',whiteSpace:'nowrap',textAlign:'center'}
  const thSL={...thS,textAlign:'left',minWidth:130}
  const tdS={padding:'6px 8px',fontSize:11,borderBottom:'1px solid #E5E7EB',textAlign:'center',fontFamily:'monospace'}
  const tdSL={...tdS,textAlign:'left',fontWeight:700,color:'#1B3A6B',background:'#F8FAFF'}

  // totals
  const totalCashIn = months.reduce((s,ym)=>s+(data[ym]?.planned_cash_in||0),0)
  const totalPlannedBudget = months.reduce((s,ym)=>s+(data[ym]?.planned_budget||0),0)
  const totalInvoiced = months.reduce((s,ym)=>s+(data[ym]?.client_invoice||0),0)
  const totalCertified = months.reduce((s,ym)=>s+(data[ym]?.certified_amount||0),0)
  const totalActualProgress  = months.reduce((s,ym)=>s+(data[ym]?.actual_progress||0),0)
  const totalPlannedProgress = months.reduce((s,ym)=>s+(data[ym]?.planned_progress||0),0)

  if(!projectId) return(
    <div style={{fontFamily:'inherit',background:'#F3F4F6',minHeight:'100vh',padding:32}}>
      <h1 style={{fontSize:20,fontWeight:800,color:'#0F1017',marginBottom:4}}>Scheduling</h1>
      <p style={{fontSize:12,color:'#6B7280',marginBottom:24}}>اختر المشروع</p>
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))',gap:12,maxWidth:800}}>
        {projects.map(p=>(
          <button key={p.id} onClick={()=>{ setProjectId(p.id); setProjectName(p.name) }}
            style={{textAlign:'left',background:'#fff',border:'1px solid #E5E7EB',borderRadius:10,padding:'14px 16px',cursor:'pointer',fontFamily:'inherit'}}>
            <p style={{fontWeight:700,color:'#0F1017',fontSize:13}}>{p.name}</p>
            <p style={{fontSize:11,color:'#6B7280',marginTop:2}}>{p.organizations?.name}</p>
          </button>
        ))}
      </div>
    </div>
  )

  return(
    <div style={{fontFamily:'inherit',background:'#F3F4F6',minHeight:'100vh'}}>
      {/* Header */}
      <div style={{background:'#fff',borderBottom:'1px solid #E5E7EB',padding:'18px 24px 14px',display:'flex',alignItems:'center',justifyContent:'space-between',gap:16}}>
        <div>
          <h1 style={{fontSize:20,fontWeight:800,color:'#0F1017',letterSpacing:'-.02em'}}>Scheduling</h1>
          <p style={{fontSize:11.5,color:'#6B7280',marginTop:3}}>
            {projectName}
            <button onClick={()=>setProjectId(null)}
              style={{marginLeft:8,fontSize:10,color:'#9CA3AF',background:'none',border:'1px solid #E5E7EB',borderRadius:4,padding:'1px 7px',cursor:'pointer',fontFamily:'inherit'}}>
              × Change
            </button>
          </p>
        </div>
        <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
          <button onClick={()=>setShowSettings(true)}
            style={{padding:'6px 14px',border:'1px solid #E5E7EB',borderRadius:6,background:'#fff',fontSize:12,cursor:'pointer'}}>
            ⚙ {settings?.project_start_month?.slice(0,7)||'Set Period'} → {settings?.project_end_month?.slice(0,7)||''}
          </button>
          <button onClick={downloadTemplate}
            style={{padding:'6px 14px',border:'1px solid #E5E7EB',borderRadius:6,background:'#fff',fontSize:12,cursor:'pointer'}}>↓ Template</button>
          <label style={{padding:'6px 14px',border:'1px solid #E5E7EB',borderRadius:6,background:'#fff',fontSize:12,cursor:'pointer'}}>
            ↑ Upload
            <input type="file" accept=".xlsx,.xls" onChange={handleUpload} style={{display:'none'}}/>
          </label>
          {Object.keys(dirty).length>0 && (
            <button onClick={saveAll} disabled={saving}
              style={{padding:'6px 18px',background:'#1B3A6B',color:'#fff',borderRadius:6,border:'none',fontSize:12,fontWeight:700,cursor:'pointer'}}>
              {saving?'⏳ Saving...':`💾 Save (${Object.keys(dirty).length})`}
            </button>
          )}
        </div>
      </div>

      <div style={{padding:'20px 24px'}}>
        {/* KPI Cards */}
        {months.length>0 && (
          <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:12,marginBottom:20}}>
            <div style={{background:'#fff',border:'1px solid #E5E7EB',borderRadius:10,padding:'16px 18px',borderTop:'3px solid #1B3A6B'}}>
              <p style={{fontSize:9,fontWeight:700,textTransform:'uppercase',letterSpacing:'.12em',color:'#6B7280',marginBottom:6}}>Total Planned Cash In</p>
              <p style={{fontSize:20,fontWeight:800,color:'#1B3A6B',fontVariantNumeric:'tabular-nums'}}>SAR {fmt(totalCashIn)}</p>
            </div>
            <div style={{background:'#fff',border:'1px solid #E5E7EB',borderRadius:10,padding:'16px 18px',borderTop:'3px solid #7C3AED'}}>
              <p style={{fontSize:9,fontWeight:700,textTransform:'uppercase',letterSpacing:'.12em',color:'#6B7280',marginBottom:6}}>Total Planned Budget</p>
              <p style={{fontSize:20,fontWeight:800,color:'#7C3AED',fontVariantNumeric:'tabular-nums'}}>SAR {fmt(totalPlannedBudget)}</p>
            </div>
            <div style={{background:'#fff',border:'1px solid #E5E7EB',borderRadius:10,padding:'16px 18px',borderTop:'3px solid #0369a1'}}>
              <p style={{fontSize:9,fontWeight:700,textTransform:'uppercase',letterSpacing:'.12em',color:'#6B7280',marginBottom:6}}>Total Client Invoiced</p>
              <p style={{fontSize:20,fontWeight:800,color:'#0369a1',fontVariantNumeric:'tabular-nums'}}>SAR {fmt(totalInvoiced)}</p>
            </div>
            <div style={{background:'#fff',border:'1px solid #E5E7EB',borderRadius:10,padding:'16px 18px',borderTop:'3px solid #16a34a'}}>
              <p style={{fontSize:9,fontWeight:700,textTransform:'uppercase',letterSpacing:'.12em',color:'#6B7280',marginBottom:6}}>Total Actual Progress</p>
              <p style={{fontSize:20,fontWeight:800,color:'#16a34a'}}>{totalActualProgress.toFixed(1)}%</p>
            </div>
            <div style={{background:'#fff',border:'1px solid #E5E7EB',borderRadius:10,padding:'16px 18px',borderTop:'3px solid #d97706'}}>
              <p style={{fontSize:9,fontWeight:700,textTransform:'uppercase',letterSpacing:'.12em',color:'#6B7280',marginBottom:6}}>Total Planned Progress</p>
              <p style={{fontSize:20,fontWeight:800,color:'#d97706'}}>{totalPlannedProgress.toFixed(1)}%</p>
            </div>
          </div>
        )}

        {!settings?.project_start_month ? (
          <div style={{padding:40,textAlign:'center',background:'#fff',borderRadius:10,border:'1px solid #E5E7EB'}}>
            <p style={{color:'#6B7280',marginBottom:16,fontSize:14}}>حدد أولاً فترة المشروع لتظهر الشهور</p>
            <button onClick={()=>setShowSettings(true)}
              style={{padding:'10px 24px',background:'#1B3A6B',color:'#fff',borderRadius:8,border:'none',cursor:'pointer',fontWeight:700,fontSize:13}}>
              ⚙ تحديد فترة المشروع
            </button>
          </div>
        ) : (
          <div style={{overflowX:'auto',borderRadius:10,border:'1px solid #E5E7EB',boxShadow:'0 1px 4px rgba(0,0,0,.06)',background:'#fff'}}>
            <table style={{borderCollapse:'collapse',width:'100%'}}>
              <thead>
                <tr>
                  <th style={{...thSL,position:'sticky',left:0,zIndex:2}}>Row</th>
                  {months.map(ym=>(
                    <th key={ym} style={{...thS,minWidth:100}}>
                      <div>{monthLabel(ym)}</div>
                    </th>
                  ))}
                  <th style={{...thS,background:'#0F2A50',minWidth:100}}>Total</th>
                </tr>
              </thead>
              <tbody>
                {/* Planned Cash In */}
                <tr>
                  <td style={{...tdSL,position:'sticky',left:0}}>📥 Planned Cash In</td>
                  {months.map(ym=>(
                    <td key={ym} style={{padding:3,borderBottom:'1px solid #E5E7EB'}}>
                      <input type="number" value={data[ym]?.planned_cash_in||''}
                        onChange={e=>handleCell(ym,'planned_cash_in',e.target.value)}
                        style={{width:94,padding:'4px 6px',fontSize:11,border:dirty[`${ym}|planned_cash_in`]?'1px solid #1B3A6B':'1px solid #E5E7EB',borderRadius:4,textAlign:'right',fontFamily:'monospace',background:dirty[`${ym}|planned_cash_in`]?'#EFF6FF':'#fff'}}/>
                    </td>
                  ))}
                  <td style={{...tdS,fontWeight:700,color:'#1B3A6B',background:'#EFF6FF'}}>{fmt(totalCashIn)}</td>
                </tr>

                {/* Planned Budget — separate from Planned Cash In (Cost vs Cash) */}
                <tr style={{background:'#FAF5FF'}}>
                  <td style={{...tdSL,position:'sticky',left:0,background:'#FAF5FF',color:'#7C3AED'}}>🏗️ Planned Budget</td>
                  {months.map(ym=>(
                    <td key={ym} style={{padding:3,borderBottom:'1px solid #E5E7EB',background:'#FAF5FF'}}>
                      <input type="number" value={data[ym]?.planned_budget||''}
                        onChange={e=>handleCell(ym,'planned_budget',e.target.value)}
                        style={{width:94,padding:'4px 6px',fontSize:11,border:dirty[`${ym}|planned_budget`]?'1px solid #7C3AED':'1px solid #E5E7EB',borderRadius:4,textAlign:'right',fontFamily:'monospace',background:dirty[`${ym}|planned_budget`]?'#FAF5FF':'#fff'}}/>
                    </td>
                  ))}
                  <td style={{...tdS,fontWeight:700,color:'#7C3AED',background:'#FAF5FF'}}>{fmt(totalPlannedBudget)}</td>
                </tr>

                {/* Client Invoice */}
                <tr style={{background:'#F0F9FF'}}>
                  <td style={{...tdSL,position:'sticky',left:0,background:'#F0F9FF',color:'#0369a1'}}>🧾 Client Invoice</td>
                  {months.map(ym=>(
                    <td key={ym} style={{padding:3,borderBottom:'1px solid #E5E7EB',background:'#F0F9FF'}}>
                      <input type="number" value={data[ym]?.client_invoice||''}
                        onChange={e=>handleCell(ym,'client_invoice',e.target.value)}
                        style={{width:94,padding:'4px 6px',fontSize:11,border:dirty[`${ym}|client_invoice`]?'1px solid #0369a1':'1px solid #E5E7EB',borderRadius:4,textAlign:'right',fontFamily:'monospace',background:dirty[`${ym}|client_invoice`]?'#F0F9FF':'#fff'}}/>
                    </td>
                  ))}
                  <td style={{...tdS,fontWeight:700,color:'#0369a1',background:'#F0F9FF'}}>{fmt(totalInvoiced)}</td>
                </tr>

                {/* Certified Amount — separate from Client Invoice (Certified ≠ Invoice).
                    Read automatically by the new Cash In module; not re-entered there. */}
                <tr style={{background:'#F0FDF4'}}>
                  <td style={{...tdSL,position:'sticky',left:0,background:'#F0FDF4',color:'#15803D'}}>✅ Certified Amount</td>
                  {months.map(ym=>(
                    <td key={ym} style={{padding:3,borderBottom:'1px solid #E5E7EB',background:'#F0FDF4'}}>
                      <input type="number" value={data[ym]?.certified_amount||''}
                        onChange={e=>handleCell(ym,'certified_amount',e.target.value)}
                        style={{width:94,padding:'4px 6px',fontSize:11,border:dirty[`${ym}|certified_amount`]?'1px solid #15803D':'1px solid #E5E7EB',borderRadius:4,textAlign:'right',fontFamily:'monospace',background:dirty[`${ym}|certified_amount`]?'#F0FDF4':'#fff'}}/>
                    </td>
                  ))}
                  <td style={{...tdS,fontWeight:700,color:'#15803D',background:'#F0FDF4'}}>{fmt(totalCertified)}</td>
                </tr>

                {/* Actual Progress */}
                <tr style={{background:'#F0FDF4'}}>
                  <td style={{...tdSL,position:'sticky',left:0,background:'#F0FDF4',color:'#16a34a'}}>📊 Actual Progress %</td>
                  {months.map(ym=>{
                    const v=data[ym]?.actual_progress||0
                    return(
                      <td key={ym} style={{padding:3,borderBottom:'1px solid #E5E7EB',background:'#F0FDF4'}}>
                        <div style={{position:'relative'}}>
                          <input type="number" min={0} max={100} value={v||''}
                            onChange={e=>handleCell(ym,'actual_progress',e.target.value)}
                            style={{width:94,padding:'4px 6px',fontSize:11,border:dirty[`${ym}|actual_progress`]?'1px solid #16a34a':'1px solid #E5E7EB',borderRadius:4,textAlign:'right',fontFamily:'monospace',background:dirty[`${ym}|actual_progress`]?'#F0FDF4':'#F0FDF4'}}/>
                          {v>0 && <span style={{position:'absolute',right:26,top:'50%',transform:'translateY(-50%)',fontSize:10,color:'#16a34a',pointerEvents:'none'}}>%</span>}
                        </div>
                      </td>
                    )
                  })}
                  <td style={{...tdS,background:'#F0FDF4',color:'#16a34a',fontWeight:700}}>
                    {totalActualProgress.toFixed(1)}%
                  </td>
                </tr>

                {/* Planned Progress */}
                <tr style={{background:'#FFFBEB'}}>
                  <td style={{...tdSL,position:'sticky',left:0,background:'#FFFBEB',color:'#d97706'}}>📈 Planned Progress %</td>
                  {months.map(ym=>{
                    const v=data[ym]?.planned_progress||0
                    return(
                      <td key={ym} style={{padding:3,borderBottom:'1px solid #E5E7EB',background:'#FFFBEB'}}>
                        <div style={{position:'relative'}}>
                          <input type="number" min={0} max={100} value={v||''}
                            onChange={e=>handleCell(ym,'planned_progress',e.target.value)}
                            style={{width:94,padding:'4px 6px',fontSize:11,border:dirty[`${ym}|planned_progress`]?'1px solid #d97706':'1px solid #E5E7EB',borderRadius:4,textAlign:'right',fontFamily:'monospace',background:'#FFFBEB'}}/>
                          {v>0 && <span style={{position:'absolute',right:26,top:'50%',transform:'translateY(-50%)',fontSize:10,color:'#d97706',pointerEvents:'none'}}>%</span>}
                        </div>
                      </td>
                    )
                  })}
                  <td style={{...tdS,background:'#FFFBEB',color:'#d97706',fontWeight:700}}>
                    {totalPlannedProgress.toFixed(1)}%
                  </td>
                </tr>

                {/* Progress Bar row */}
                <tr>
                  <td style={{...tdSL,position:'sticky',left:0,fontSize:10,color:'#9CA3AF'}}>Progress Bar</td>
                  {months.map(ym=>{
                    const act=data[ym]?.actual_progress||0
                    const pln=data[ym]?.planned_progress||0
                    return(
                      <td key={ym} style={{padding:'4px 6px',borderBottom:'1px solid #E5E7EB',verticalAlign:'middle'}}>
                        {(act>0||pln>0) ? (
                          <div style={{width:'100%'}}>
                            <div style={{height:6,background:'#E5E7EB',borderRadius:3,marginBottom:2,overflow:'hidden'}}>
                              <div style={{height:'100%',width:`${Math.min(pln,100)}%`,background:'#d97706',borderRadius:3}}/>
                            </div>
                            <div style={{height:6,background:'#E5E7EB',borderRadius:3,overflow:'hidden'}}>
                              <div style={{height:'100%',width:`${Math.min(act,100)}%`,background:'#16a34a',borderRadius:3}}/>
                            </div>
                          </div>
                        ) : <span style={{color:'#E5E7EB',fontSize:10}}>—</span>}
                      </td>
                    )
                  })}
                  <td style={tdS}/>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Settings Modal */}
      {showSettings && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:50}}>
          <div style={{background:'#fff',borderRadius:16,padding:32,width:380,boxShadow:'0 20px 60px rgba(0,0,0,.2)'}}>
            <h3 style={{fontSize:16,fontWeight:700,marginBottom:20}}>⚙ فترة المشروع</h3>
            <div style={{marginBottom:16}}>
              <label style={{fontSize:12,color:'#6B7280',display:'block',marginBottom:6}}>شهر البداية</label>
              <input type="month" value={settingsForm.project_start_month}
                onChange={e=>setSettingsForm(p=>({...p,project_start_month:e.target.value}))}
                style={{width:'100%',border:'1px solid #E5E7EB',borderRadius:8,padding:'8px 12px',fontSize:13,boxSizing:'border-box'}}/>
            </div>
            <div style={{marginBottom:24}}>
              <label style={{fontSize:12,color:'#6B7280',display:'block',marginBottom:6}}>شهر النهاية</label>
              <input type="month" value={settingsForm.project_end_month}
                onChange={e=>setSettingsForm(p=>({...p,project_end_month:e.target.value}))}
                style={{width:'100%',border:'1px solid #E5E7EB',borderRadius:8,padding:'8px 12px',fontSize:13,boxSizing:'border-box'}}/>
            </div>
            <div style={{display:'flex',gap:10}}>
              <button onClick={()=>setShowSettings(false)} style={{flex:1,padding:'10px',border:'1px solid #E5E7EB',borderRadius:8,cursor:'pointer',background:'#fff'}}>إلغاء</button>
              <button onClick={saveSettings} style={{flex:1,padding:'10px',background:'#1B3A6B',color:'#fff',borderRadius:8,border:'none',cursor:'pointer',fontWeight:700}}>حفظ</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
